<script setup lang="ts">
import { computed, useSlots } from 'vue';
import type { ChatbotMessageType } from '@/types/ChatbotMessageType';
import { useTranslation } from '@/composables';
import { parseMarkdown } from '@/utils/markdownUtils';
import DemoStarsAnimation from '@/assets/img/demo-stars-animation.svg?component';

const props = defineProps<{
    message: ChatbotMessageType;
}>();

const emit = defineEmits<{
    toggleReasoning: [messageId: string];
}>();

const slots = useSlots();

const { t } = useTranslation();

const isUserMessage = computed(() => props.message.sender === 'user');
const isStreaming = computed(() => props.message.isStreaming === true);
const isErrorMessage = computed(() => props.message.status === 'error');

const displayText = computed(() => {
    if (isStreaming.value && props.message.streamingBuffer) {
        return props.message.streamingBuffer;
    }

    return props.message.text;
});

const hasContentSlot = computed(() => !!slots.content);

const showMessageBubble = computed(() => {
    const hasContent = displayText.value?.trim().length > 0;
    const isStreamingWithoutContent = isStreaming.value && !hasContent;

    return hasContent || isStreamingWithoutContent || hasContentSlot.value;
});

const activeToolCalls = computed(() => {
    if (!isStreaming.value) {
        return [];
    }

    const toolCalls =
        props.message.toolCalls?.filter(
            call => call.status === 'executing' && call.toolName && call.toolName.trim().length > 0,
        ) || [];

    // Deduplicate by toolName - keep only unique tool names
    const seenNames = new Set<string>();
    const uniqueToolCalls = [];
    for (const call of toolCalls) {
        if (!seenNames.has(call.toolName)) {
            seenNames.add(call.toolName);
            uniqueToolCalls.push(call);
        }
    }

    return uniqueToolCalls;
});

const hasReasoning = computed(() => {
    return props.message.hasReceivedReasoning === true && (props.message.reasoning?.trim().length ?? 0) > 0;
});

const showPulsingReasoning = computed(() => {
    return hasReasoning.value && props.message.isReasoningCollapsed === undefined && !props.message.hasReceivedMessage;
});

const showCollapsedReasoning = computed(() => {
    return hasReasoning.value && props.message.isReasoningCollapsed !== undefined;
});

const isReasoningExpanded = computed(() => {
    return hasReasoning.value && props.message.isReasoningCollapsed === false;
});

const toggleReasoning = (): void => {
    emit('toggleReasoning', props.message.id);
};

const shouldParseMarkdown = computed(() => {
    return !isUserMessage.value && displayText.value && displayText.value.trim().length > 0;
});

const parsedMessage = computed(() => {
    const text = displayText.value;

    if (!text || text.trim().length === 0) {
        return '';
    }

    if (shouldParseMarkdown.value) {
        return parseMarkdown(text);
    }

    return text;
});
</script>

<template>
    <div class="chat-message d-flex align-start mb-3">
        <div
            class="message-content flex-grow-1"
            :class="{ 'text-right': isUserMessage }"
        >
            <!-- Pulsing reasoning: reasoning arrived, message NOT yet -->
            <div
                v-if="showPulsingReasoning"
                class="reasoning-pulsing pa-2 mb-1 text-left"
            >
                <span class="reasoning-pulsing-text">
                    {{ message.reasoning }}
                </span>
            </div>

            <!-- Collapsible reasoning section -->
            <div
                v-if="showCollapsedReasoning"
                class="reasoning-section mb-1 text-left"
            >
                <button
                    class="reasoning-toggle d-inline-flex align-center ga-1 text-caption text-grey-500"
                    type="button"
                    @click="toggleReasoning"
                >
                    <span>{{ t('chatbot.reasoningToggle') }}</span>
                    <VIcon
                        size="14"
                        :icon="isReasoningExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down'"
                    />
                </button>

                <div
                    v-if="isReasoningExpanded"
                    class="reasoning-expanded mt-1 pa-3 rounded-lg"
                >
                    <p class="reasoning-text text-body-2 text-grey-darken-1 ma-0">
                        {{ message.reasoning }}
                    </p>
                </div>
            </div>

            <div
                v-if="showMessageBubble"
                class="message-bubble pa-3 rounded-lg d-inline-block"
                :class="{
                    'message-bubble--user': isUserMessage,
                    'mr-auto': !isUserMessage && !isErrorMessage,
                    'bg-error text-white mr-auto': isErrorMessage,
                    'typing-indicator': isStreaming,
                    'empty-message': displayText?.trim().length === 0,
                }"
            >
                <div class="message-content-wrapper text-left">
                    <slot name="content">
                        <span
                            v-if="shouldParseMarkdown"
                            class="markdown-content"
                            v-html="parsedMessage"
                        ></span>
                        <span v-else-if="parsedMessage">
                            {{ parsedMessage }}
                        </span>
                    </slot>

                    <DemoStarsAnimation
                        v-if="isStreaming"
                        class="typing-stars"
                    />
                </div>
            </div>

            <div
                v-if="activeToolCalls.length > 0"
                class="tool-usage-indicator mt-1 text-left text-grey-600"
            >
                <span class="text-caption">
                    {{ t('chatbot.usingTool') }}
                    <span
                        v-for="(toolCall, index) in activeToolCalls"
                        :key="toolCall.id"
                        class="tool-name"
                    >
                        <strong>{{ toolCall.toolName }}</strong
                        ><span v-if="index < activeToolCalls.length - 1">, </span>
                    </span>
                    <span v-if="activeToolCalls.length === 1">&nbsp;{{ t('chatbot.toolSingular') }}</span>
                    <span v-else>&nbsp;{{ t('chatbot.toolPlural') }}</span>
                    <span class="loading-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                    </span>
                </span>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.reasoning-pulsing {
    max-width: 90%;

    .reasoning-pulsing-text {
        color: rgba(0, 0, 0, 0.3);
        font-size: 0.82rem;
        font-style: italic;
        animation: reasoning-pulse 2s ease-in-out infinite;
    }
}

@keyframes reasoning-pulse {
    0%,
    100% {
        opacity: 0.4;
    }
    50% {
        opacity: 1;
    }
}

.reasoning-section {
    max-width: 90%;
}

.reasoning-toggle {
    background: none;
    border: none;
    cursor: pointer;
    padding: 2px 4px;
    border-radius: 4px;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: rgba(0, 0, 0, 0.04);
    }
}

.reasoning-expanded {
    background-color: rgba(var(--v-theme-grey-25));
    border: 1px solid rgba(var(--v-theme-grey-100));
}

.reasoning-text {
    line-height: 1.5;
    white-space: pre-wrap;
}

.message-bubble {
    position: relative;
    max-width: 90%;
    word-wrap: break-word;
    color: rgba(var(--v-theme-data-table-content));
}

// Error bubble: the parent sets `text-white`, but `.markdown-content` and some of
// its nested elements define their own color — override them so the text stays white.
.message-bubble.bg-error :deep(.markdown-content) {
    color: inherit;

    strong,
    b,
    em,
    i,
    blockquote {
        color: inherit;
    }
}

.message-bubble--user {
    background-color: rgba(var(--v-theme-grey-50));
}
.typing-stars {
    display: block;
    width: 32px;
    height: 32px;
    margin-top: 4px;

    :deep(.sparkle) {
        transform-origin: center;
        will-change: transform, opacity;
        animation:
            stars-float 3.8s ease-in-out infinite,
            stars-think 1.8s ease-in-out infinite;
        opacity: 0.7;

        &.small {
            fill: #62c6c7;
            animation-delay: 0s, 0s;
        }

        &.medium {
            fill: #ef4370;
            animation-delay: 0.15s, 0.35s;
        }

        &.large {
            fill: #62c6c7;
            animation-delay: 0.3s, 0.7s;
        }
    }
}

.typing-indicator.empty-message {
    .typing-stars {
        margin-top: 0;
    }
}

@keyframes stars-float {
    0% {
        transform: translateY(0) scale(1);
    }
    50% {
        transform: translateY(-4px) scale(1.03);
    }
    100% {
        transform: translateY(0) scale(1);
    }
}

@keyframes stars-think {
    0% {
        opacity: 0.45;
    }
    30% {
        opacity: 0.7;
    }
    50% {
        opacity: 1;
    }
    70% {
        opacity: 0.7;
    }
    100% {
        opacity: 0.45;
    }
}

.streaming-cursor {
    .cursor-blink {
        animation: blink 1s infinite;
        font-weight: bold;
        margin-left: 2px;
    }
}

@keyframes blink {
    0%,
    50% {
        opacity: 1;
    }
    51%,
    100% {
        opacity: 0;
    }
}

.tool-usage-indicator {
    .loading-dots {
        display: inline-flex;
        align-items: center;
        gap: 2px;
        margin-left: 4px;

        span {
            width: 4px;
            height: 4px;
            border-radius: 50%;
            background-color: currentColor;
            opacity: 0.4;
            animation: bounce 1.4s infinite;

            &:nth-child(1) {
                animation-delay: 0s;
            }

            &:nth-child(2) {
                animation-delay: 0.2s;
            }

            &:nth-child(3) {
                animation-delay: 0.4s;
            }
        }
    }
}

@keyframes bounce {
    0%,
    60%,
    100% {
        transform: translateY(0);
        opacity: 0.4;
    }
    30% {
        transform: translateY(-8px);
        opacity: 1;
    }
}

:deep(.markdown-content) {
    line-height: 1.6;
    color: rgba(var(--v-theme-data-table-content));
    font-size: 0.95rem;

    h1,
    h2,
    h3,
    h4,
    h5,
    h6 {
        margin: 18px 0 10px 0;
        line-height: 1.25;
        font-weight: 500;

        &:first-child {
            margin-top: 0;
        }

        &:last-child {
            margin-bottom: 8px;
        }
    }

    h1 {
        font-size: 1.6rem;
        font-weight: 600;
        margin-bottom: 16px;
    }

    h2 {
        font-size: 1.4rem;
        margin-top: 3rem;
    }

    h3 {
        font-size: 1.2rem;
    }

    h4,
    h5,
    h6 {
        font-size: 1rem;
        font-weight: 500;
    }

    p {
        margin: 12px 0;
        line-height: 1.7;

        &:first-child {
            margin-top: 0;
        }

        &:last-child {
            margin-bottom: 0;
        }
    }

    ul,
    ol {
        margin: 12px 0;
        padding-left: 28px;

        &:first-child {
            margin-top: 0;
        }

        &:last-child {
            margin-bottom: 0;
        }

        li {
            margin: 6px 0;
            line-height: 1.6;
        }

        ul,
        ol {
            margin: 6px 0;
            padding-left: 24px;
        }
    }

    table {
        width: 100%;
        border-collapse: collapse;
        margin: 16px 0;
        background: rgba(255, 255, 255, 0.8);
        border-radius: 8px;
        overflow: hidden;
        font-size: 0.9rem;

        &:first-child {
            margin-top: 0;
        }

        &:last-child {
            margin-bottom: 0;
        }

        th,
        td {
            text-align: left;
            padding: 12px 16px;
            border-bottom: 1px solid rgba(0, 0, 0, 0.08);
            vertical-align: top;
        }

        th {
            background: rgba(var(--v-theme-primary), 0.08);
            font-weight: 600;
            border-bottom: 2px solid rgba(var(--v-theme-primary), 0.15);
        }

        tr:nth-child(even) td {
            background: rgba(0, 0, 0, 0.02);
        }

        tr:last-child {
            th,
            td {
                border-bottom: none;
            }
        }
    }

    code {
        padding: 3px 6px;
        border-radius: 4px;
        font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
        font-size: 0.88rem;
        font-weight: 500;
        border: 1px solid rgba(var(--v-theme-primary), 0.15);
    }

    pre {
        background: rgba(0, 0, 0, 0.04);
        border: 1px solid rgba(0, 0, 0, 0.08);
        padding: 16px;
        border-radius: 8px;
        margin: 16px 0;
        overflow-x: auto;
        box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.06);

        &:first-child {
            margin-top: 0;
        }

        &:last-child {
            margin-bottom: 0;
        }

        code {
            background: none;
            border: none;
            padding: 0;
            color: inherit;
            font-size: 0.9rem;
        }
    }

    blockquote {
        border-left: 4px solid rgba(var(--v-theme-primary), 0.3);
        margin: 16px 0;
        padding: 8px 20px;
        font-style: italic;
        background: rgba(var(--v-theme-primary), 0.04);
        border-radius: 0 6px 6px 0;
        color: rgba(0, 0, 0, 0.8);

        &:first-child {
            margin-top: 0;
        }

        &:last-child {
            margin-bottom: 0;
        }

        p {
            margin: 8px 0;

            &:first-child {
                margin-top: 0;
            }

            &:last-child {
                margin-bottom: 0;
            }
        }
    }

    a {
        color: rgb(var(--v-theme-primary));
        text-decoration: underline;
        transition: all 0.2s ease;

        &:hover {
            text-decoration: none;
            color: rgba(var(--v-theme-primary), 0.8);
        }
    }

    strong,
    b {
        font-weight: 700;
        color: rgba(var(--v-theme-data-table-content));
    }

    em,
    i {
        font-style: italic;
        color: rgba(0, 0, 0, 0.85);
    }

    // Horizontal rule
    hr {
        border: none;
        border-top: 2px solid rgba(var(--v-theme-primary), 0.15);
        margin: 20px 0;
    }

    // Responsive design improvements
    @media (max-width: 768px) {
        font-size: 0.9rem;

        table {
            font-size: 0.8rem;
            margin: 12px -8px;
            width: calc(100% + 16px);
            border-radius: 0;
        }

        th,
        td {
            padding: 8px 10px;
        }

        pre {
            margin: 12px -8px;
            width: calc(100% + 16px);
            border-radius: 0;
            padding: 12px;
        }

        h1 {
            font-size: 1.4rem;
        }
        h2 {
            font-size: 1.25rem;
        }
        h3 {
            font-size: 1.1rem;
        }
    }

    @media (max-width: 480px) {
        ul,
        ol {
            padding-left: 20px;
        }

        blockquote {
            margin: 12px -4px;
            padding: 8px 16px;
        }
    }
}
</style>
